import { useEffect, useState } from "react";

import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { PopulationBasedFundingRegistrationPopup } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingRegistrationPopup";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { PopulationBasedFundingPortfolioTableHelper } from "@/Portfolio/Helpers/PopulationBasedFundingPortfolioTableHelper";
import { PopulationBasedFundingPortfolioEvent } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingPortfolioEvent";
import { PopulationBasedFundingPortfolioActions } from "@/Portfolio/Services/PopulationBasedFundingPortfolioService";
import { PopulationBasedFundingPortfolioOverridePopup } from "./PopulationBasedFundingPortfolioOverridePopup";
import { PopulationBasedFundingPortfolioSettings } from "./PopulationBasedFundingPortfolioSettings";
import { PopulationBasedFundingTransferPatientRegisterPopup } from "./PopulationBasedFundingTransferPatientRegisterPopup";

function PopulationBasedFundingPortfolioTable(): JSX.Element {
    const helper = new PopulationBasedFundingPortfolioTableHelper();

    const { getText } = useTranslation();

    const [showFilterSettings, setShowFilterSettings] =
        useState(false);

    const [showRegistrationPopup, setShowRegistrationPopup] =
        useState(false);

    const [showOverrideProposalPopup, setShowOverrideProposalPopup] =
        useState(false);

    const [showTasksListPopup, setShowTasksListPopup] =
        useState(false);

    const [showTransferPatientRegister, setShowTransferPatientRegister] =
        useState(false);

    const [populationBasedFundingPortfolioModel, setPopulationBasedFundingPortfolioModel] =
        useState(portfolioStore.populationBasedFundingPortfolioModel);

    const [isColumnOrderingPopupVisible, setColumnOrderingPopupIsVisible] =
        useState(false);

    const { Data, FilterSettings, EventToOverride, TasksByEventIdDictionnary } = portfolioStore.populationBasedFundingPortfolioModel;

    const tableItems: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>[] =
        Data?.map(event => {
            event.Tasks = TasksByEventIdDictionnary?.[event.PatientContactId];
            return new TableDataSourceItem(event);
        }) ?? [];

    const onPrint = async (): Promise<void> => {
        const response = await PopulationBasedFundingPortfolioActions
            .printPatientRegistry(
                populationBasedFundingPortfolioModel.FilterSettings
                , populationBasedFundingPortfolioModel.QueryOrderType
                , populationBasedFundingPortfolioModel.QueryOrderDirection
            );

        if (response.IsSuccess)
            PrintHelper.ShowPrintPreviewFileName(response.fileName);
    };

    const onExportCsv = async (): Promise<void> => {
        const downloadPatientRegisterCsvUrl = apiSettings.buildAppUrl("api/population-based-funding/fetchPatientRegisterCsv", { auth: true, noCache: true });
        window.open(downloadPatientRegisterCsvUrl);
    };

    useOnStoreChange(portfolioStore, () => onEventDataChanged());

    useOnStoreChange(portfolioStore, () => onEventToOverrideChanged());

    useOnStoreChange(portfolioStore, () => onNewPatientToRegisterChanged());

    useOnStoreChange(portfolioStore, () => onClickedEventTasksCounterChanged());

    useEffect(
        () => {
            $(window).trigger("resize");

            PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents(
                populationBasedFundingPortfolioModel.FilterSettings
            ).then(events => {
                PopulationBasedFundingPortfolioActions.loadTasksForPatientContactId(events.map(event => event.PatientContactId));
            });
        },
        []
    );

    const onMassAcceptClick = (): Promise<void> => PopulationBasedFundingPortfolioActions.massAcceptProposal(populationBasedFundingPortfolioModel.SelectedEventIds);

    const onEventDataChanged = (): void => {
        setPopulationBasedFundingPortfolioModel(portfolioStore.populationBasedFundingPortfolioModel);
    };

    const onNewPatientToRegisterChanged = (): void => {
        const execute = async (): Promise<void> => {
            const popUpIsClosing = portfolioStore.populationBasedFundingPortfolioModel.NewPatientToRegister == null;
            const setState = (): void => setShowRegistrationPopup(portfolioStore.populationBasedFundingPortfolioModel.NewPatientToRegister != null);

            if (!popUpIsClosing && await PopulationBasedFundingActions.showBillingLoginfNotLoggedInAsync(setState))
                return;

            setState();
        };

        execute();
    };

    const onEventToOverrideChanged = (): void => {
        const execute = async (): Promise<void> => {
            const popUpIsClosing = portfolioStore.populationBasedFundingPortfolioModel.EventToOverride == null;
            const setState = (): void => setShowOverrideProposalPopup(portfolioStore.populationBasedFundingPortfolioModel.EventToOverride != null);

            if (!popUpIsClosing && await PopulationBasedFundingActions.showBillingLoginfNotLoggedInAsync(setState))
                return;

            setState();
        };

        execute();
    };

    const onClickedEventTasksCounterChanged = (): void => {
        setShowTasksListPopup(portfolioStore.populationBasedFundingPortfolioModel.ClickedEventTasksCounter != null);
    };

    const onCloseOverridePopup = (): void => {
        PopulationBasedFundingPortfolioActions.closeOverrideProposalPopup();
    };

    const onCloseRegistrationPopup = (): void => {
        PopulationBasedFundingPortfolioActions.closeRegisterNewPatientPopup();
    };

    const closeTasksListPopup = (): void => {
        PopulationBasedFundingPortfolioActions.closeTasksListPopup();
    };

    const onSubmitRegistration = async (formData: PopulationBasedFundingFormData): Promise<boolean> => {
        const response = await PopulationBasedFundingPortfolioActions.acceptInternalRegistrationProposal(
            portfolioStore.populationBasedFundingPortfolioModel.InternalProposalToCompleteEventId,
            formData
        );

        if (response?.IsSuccess) {
            PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents();
            onCloseRegistrationPopup();
        }

        return response?.IsSuccess ?? false;
    };

    const saveOverrideRegistration = async (selectedReason: number, customReason: string, note: string, eventId: string): Promise<void> => {
        await PopulationBasedFundingPortfolioActions.overrideRegistrationProposalAndLoadHistory(
            selectedReason,
            customReason,
            note,
            eventId
        );
    };

    const saveOverrideDeregistration = async (selectedReason: number, customReason: string, note: string, eventId: string): Promise<void> => {
        await PopulationBasedFundingPortfolioActions.overrideDeregistrationProposalAndLoadHistory(
            selectedReason,
            customReason,
            note,
            eventId
        );
    };

    const saveTransferPatientRegister = async (sourcePhysicianContactFacetId: string, targetPhysicianContactFacetId: string): Promise<boolean> => {
        const response = await PopulationBasedFundingPortfolioActions.transferPatientRegister(
            sourcePhysicianContactFacetId,
            targetPhysicianContactFacetId
        );

        if (response) {
            PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents();
        }

        return response;
    };

    const openTransferPatientRegisterPopup = (): void => setShowTransferPatientRegister(true);

    const transferPatientsButton: JSX.Element = (
        <button key="openTransferPatientRegisterPopup" className="button control" onClick={openTransferPatientRegisterPopup}>
            <i className="fa fa-arrows-h" data-cy="fa-arrows-h" />
        </button>
    );

    return (
        <>
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={getText("PatientRegisterLabel")}
                    searchText={FilterSettings.SearchText}
                    searchPlaceholder={getText("SearchLabel")}
                    onSearch={text => PopulationBasedFundingPortfolioActions.updateSearchText(text)}
                    onRefreshClick={() => PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents()}
                    onSettingsClick={() => setShowFilterSettings(!showFilterSettings)}
                    hideRightSideComponents={false}
                    onPrintClick={onPrint}
                    onExportCsvClick={onExportCsv}
                    additionalRightComponent={[transferPatientsButton]}
                />
                <div id="posref" className="ws-scrollable">
                    {showFilterSettings && (
                        <PopulationBasedFundingPortfolioSettings
                            onClose={() => setShowFilterSettings(false)}
                            filterSettings={FilterSettings}
                            onColumnOrderingPopupOpenClick={() => {
                                setShowFilterSettings(false);
                                setColumnOrderingPopupIsVisible(true);
                            }}
                        />
                    )}

                    {isColumnOrderingPopupVisible && (
                        <MyleTableColumnOrderControlv2
                            TableColumns={helper.getColumnOptionsForOrderColumnsPopup()}
                            SaveOptionskeyTarget={helper.saveOptionKey}
                            ContainerId="populationBasedFundingSettingsPopup"
                            closeColumnOrderPopup={() => setColumnOrderingPopupIsVisible(false)}
                        />
                    )}

                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={helper.getTableRenderOptions()}
                            TableId="portfolio_population_based_funding"
                            Columns={helper.getTableColumns()}
                            DataSource={tableItems.toImmutable()}
                            SaveOptionskeyTarget={helper.saveOptionKey}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={FilterSettings.QueryPageSize}
                                TotalRows={FilterSettings.QueryRowCount}
                                CurrentPage={FilterSettings.QueryCurrentPageNumber}
                                changePageFunction={(nextPageNumber: number) => PopulationBasedFundingPortfolioActions.changePage(nextPageNumber)}
                            />
                        </div>
                        <div className="col-lg-2 col-md-3 col-sm-4 text-right">
                            <ButtonControl
                                onClick={onMassAcceptClick}
                                disabled={populationBasedFundingPortfolioModel.IsMassAcceptDisabled
                                    || !portfolioStore.populationBasedFundingPortfolioModel.SelectedEventIds.any()
                                }
                                label={getText("AcceptLabel")}
                                className="btn btn-primary"
                            />
                        </div>
                    </div>
                </div>
            </div>
            {showRegistrationPopup && (
                <PopulationBasedFundingRegistrationPopup
                    currentStarredPhysician={portfolioStore.populationBasedFundingPortfolioModel.NewPatientToRegister?.HasDoctor()
                        ? portfolioStore.populationBasedFundingPortfolioModel.NewPatientToRegister?.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee.toPerson()
                        : null
                    }
                    onClose={onCloseRegistrationPopup}
                    onTrySubmit={onSubmitRegistration}
                />
            )}
            {showOverrideProposalPopup && (
                <PopulationBasedFundingPortfolioOverridePopup
                    eventModel={EventToOverride}
                    onClose={() => onCloseOverridePopup()}
                    onSaveRegistration={(selectedReason: number, customReason: string, note: string, eventId: string) =>
                        saveOverrideRegistration(selectedReason, customReason, note, eventId)
                    }
                    onSaveDeregistration={(selectedReason: number, customReason: string, note: string, eventId: string) =>
                        saveOverrideDeregistration(selectedReason, customReason, note, eventId)
                    }
                />
            )}
            {showTasksListPopup && (
                <AttachedCommunicationsPopup
                    controlId="attached_tasks_portfolio"
                    topPosition={portfolioStore.populationBasedFundingPortfolioModel.ClickedTasksCounterPosition.y}
                    leftPosition={portfolioStore.populationBasedFundingPortfolioModel.ClickedTasksCounterPosition.x - 600}
                    attachedCommunications={portfolioStore.populationBasedFundingPortfolioModel.ClickedEventTasksCounter?.Tasks}
                    context={AttachedCommunicationType.Task}
                    handleRedirect={attachedCommunication => {
                        PopulationBasedFundingPortfolioActions.redirectToClickedAttachedTaskDetails(
                            portfolioStore.populationBasedFundingPortfolioModel.ClickedEventTasksCounter?.PatientContactFacetId,
                            attachedCommunication.IndividualMessageId
                        );
                        closeTasksListPopup();
                    }}
                    handleClose={closeTasksListPopup}
                />
            )}
            {showTransferPatientRegister && (
                <PopulationBasedFundingTransferPatientRegisterPopup
                    onClose={() => setShowTransferPatientRegister(false)}
                    onTrySubmit={saveTransferPatientRegister}
                />
            )}
        </>
    );
}

export { PopulationBasedFundingPortfolioTable };
